/**
 * Maps AG-Grid filter/sort state to URL params (useSearchParamsState / Findings-style).
 * URL uses flat keys; ServerSideDataSource maps filter models to API query params.
 *
 * Primary filter pattern: when `insight=<key>` is in the URL, it represents an active
 * insight tile filter. It survives sort changes and column filters added after the tile was
 * clicked; clicking a tile writes a fresh query (sort + insight) so pre-existing column filters
 * cannot be ANDed with the tile's own filter, and only explicit clearing (dismiss button or
 * clicking a different tile) removes the insight.
 */

export type InsightKey =
  | 'usersWithoutAssets'
  | 'usersInactiveLogin'
  | 'unassignedAssets'
  | 'noEdrCoverage'
  | 'noEvmCoverage'
  | 'noCommsProfile';

export const INSIGHT_LABEL_MAP: Record<InsightKey, string> = {
  usersWithoutAssets: 'COMPLIANCE_TOOLING_USERS_WITHOUT_ASSETS_LABEL',
  usersInactiveLogin: 'COMPLIANCE_TOOLING_USERS_INACTIVE_IDP_LOGIN_LABEL',
  unassignedAssets: 'COMPLIANCE_TOOLING_UNASSIGNED_ASSETS_LABEL',
  noEdrCoverage: 'COMPLIANCE_TOOLING_NO_EDR_COVERAGE_LABEL',
  noEvmCoverage: 'COMPLIANCE_TOOLING_NO_EVM_COVERAGE_LABEL',
  noCommsProfile: 'COMPLIANCE_TOOLING_NO_COMMUNICATIONS_PROFILE_LABEL',
};

export const INSIGHT_DESCRIPTION_MAP: Record<InsightKey, string> = {
  usersWithoutAssets: 'COMPLIANCE_TOOLING_USERS_WITHOUT_ASSETS_DESCRIPTION',
  usersInactiveLogin: 'COMPLIANCE_TOOLING_USERS_INACTIVE_IDP_LOGIN_DESCRIPTION',
  unassignedAssets: 'COMPLIANCE_TOOLING_UNASSIGNED_ASSETS_DESCRIPTION',
  noEdrCoverage: 'COMPLIANCE_TOOLING_NO_EDR_COVERAGE_DESCRIPTION',
  noEvmCoverage: 'COMPLIANCE_TOOLING_NO_EVM_COVERAGE_DESCRIPTION',
  noCommsProfile: 'COMPLIANCE_TOOLING_NO_COMMUNICATIONS_PROFILE_DESCRIPTION',
};
export const INSIGHT_FILTER_MODELS: Record<InsightKey, Record<string, any>> = {
  usersWithoutAssets: {
    numberOfEndpoints: {
      filterType: 'userAsset',
      assetStatus: 'Assigned',
      userAssetRelationship: 'Missing',
    },
  },
  usersInactiveLogin: {
    identityAndAccess: {
      filterType: 'identityColumn',
      filterValues: {
        activeProfile: 'missingActive',
        recentActivity: 'inactive',
      },
    },
  },
  unassignedAssets: {
    numberOfEndpoints: {
      filterType: 'userAsset',
      assetStatus: 'Unassigned',
    },
  },
  noEvmCoverage: {
    vulnerabilityManagement: {
      filterType: 'vulnerabilityCoverage',
      value: 'missing',
    },
  },
  noEdrCoverage: {
    endpointManagement: {
      filterType: 'endpointManagementCoverage',
      value: 'missing',
    },
  },
  noCommsProfile: {
    collaboration: {
      filterType: 'collaborationColumn',
      filterValues: {
        slackProfile: 'inactive',
        teamsProfile: 'inactive',
      },
    },
  },
};

export type ToolingGridState = {
  sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  filterModel: Record<string, any>;
};

const ALL = 'all';
const ATTRIBUTION_CONFLICT_LABEL = 'Conflict';
const ATTRIBUTION_NO_CONFLICT_LABEL = 'No Conflict';

export function mapGridStateToUrlParams(gridState: ToolingGridState): Record<string, any> {
  const params: Record<string, any> = {};
  if (Array.isArray(gridState.sortModel) && gridState.sortModel.length > 0) {
    const s = gridState.sortModel[0];
    params.sortColumn = s.colId;
    params.sortOrder = s.sort?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
  }

  const fm = gridState.filterModel || {};

  const nameF = fm.name;
  if (nameF?.filterType === 'text' && typeof nameF.filter === 'string' && nameF.filter.trim()) {
    params.name = nameF.filter.trim();
  }

  const deptF = fm.department;
  if (deptF?.filterType === 'departmentMultiSelect' && Array.isArray(deptF.values) && deptF.values.length > 0) {
    params.department = deptF.values.join(',');
  }

  const ua = fm.numberOfEndpoints;
  if (ua?.filterType === 'userAsset') {
    if (ua.assetStatus === 'Assigned') {
      params.assignmentFilter = 'assigned';
    } else if (ua.assetStatus === 'Unassigned') {
      params.assignmentFilter = 'unassigned';
    }
    if (ua.userAssetRelationship && ua.userAssetRelationship !== ALL) {
      params.device = ua.userAssetRelationship;
    }
  }

  const idF = fm.identityAndAccess;
  if (idF?.filterType === 'identityColumn' && idF.filterValues) {
    const v = idF.filterValues;
    if (v.activeProfile && v.activeProfile !== ALL) {
      params.identityActiveProfile = v.activeProfile;
    }
    if (v.recentActivity && v.recentActivity !== ALL) {
      params.identityRecentActivity = v.recentActivity;
    }
    if (v.mfaStatus && v.mfaStatus !== ALL) {
      params.identityMfaStatus = v.mfaStatus;
    }
  }

  const colF = fm.collaboration;
  if (colF?.filterType === 'collaborationColumn' && colF.filterValues) {
    const v = colF.filterValues;
    if (v.slackProfile && v.slackProfile !== ALL) {
      params.collaborationSlackProfile = v.slackProfile;
    }
    if (v.teamsProfile && v.teamsProfile !== ALL) {
      params.collaborationTeamsProfile = v.teamsProfile;
    }
  }

  const trF = fm.securityTraining;
  if (trF?.filterType === 'trainingColumn' && trF.filterValues?.trainingStatus && trF.filterValues.trainingStatus !== ALL) {
    params.trainingTrainingStatus = trF.filterValues.trainingStatus;
  }

  const edrF = fm.endpointManagement;
  if (edrF?.filterType === 'endpointManagementCoverage' && edrF.value === 'missing') {
    params.coverageEndpointManagement = 'missing';
  }

  const evmF = fm.vulnerabilityManagement;
  if (evmF?.filterType === 'vulnerabilityCoverage' && evmF.value === 'missing') {
    params.coverageVulnerability = 'missing';
  }

  const attributionF = fm.attributionStatus;
  if (attributionF?.filterType === 'set' && Array.isArray(attributionF.values)) {
    const values = attributionF.values.filter((value: unknown): value is string => typeof value === 'string');
    if (values.length > 0) {
      params.attributionStatus = values.join(',');
    }
  }

  return params;
}

export function mapUrlParamsToGridState(urlFilter: Record<string, any>): ToolingGridState {
  const sortModel: ToolingGridState['sortModel'] = [];
  if (urlFilter.sortColumn && urlFilter.sortOrder) {
    sortModel.push({
      colId: String(urlFilter.sortColumn),
      sort: urlFilter.sortOrder === 'DESC' ? 'desc' : 'asc',
    });
  }

  const filterModel: Record<string, any> = {};

  const nameFromUrl = urlFilter.name ?? urlFilter.q;
  if (nameFromUrl !== undefined && nameFromUrl !== null && String(nameFromUrl).trim() !== '') {
    filterModel.name = {
      filterType: 'text',
      type: 'contains',
      filter: String(nameFromUrl),
    };
  }

  if (urlFilter.department !== undefined && urlFilter.department !== null && String(urlFilter.department).trim() !== '') {
    const deptValues = String(urlFilter.department)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (deptValues.length > 0) {
      filterModel.department = {
        filterType: 'departmentMultiSelect',
        values: deptValues,
      };
    }
  }

  if (urlFilter.assignmentFilter || urlFilter.device) {
    const m: Record<string, string> = { filterType: 'userAsset' };
    if (urlFilter.assignmentFilter === 'assigned') {
      m.assetStatus = 'Assigned';
    } else if (urlFilter.assignmentFilter === 'unassigned') {
      m.assetStatus = 'Unassigned';
    }
    if (urlFilter.device) {
      m.userAssetRelationship = String(urlFilter.device);
    }
    filterModel.numberOfEndpoints = m;
  }

  if (urlFilter.identityActiveProfile || urlFilter.identityRecentActivity || urlFilter.identityMfaStatus) {
    filterModel.identityAndAccess = {
      filterType: 'identityColumn',
      filterValues: {
        activeProfile: urlFilter.identityActiveProfile,
        recentActivity: urlFilter.identityRecentActivity,
      },
    };
  }

  if (urlFilter.collaborationSlackProfile || urlFilter.collaborationTeamsProfile) {
    filterModel.collaboration = {
      filterType: 'collaborationColumn',
      filterValues: {
        slackProfile: urlFilter.collaborationSlackProfile,
        teamsProfile: urlFilter.collaborationTeamsProfile,
      },
    };
  }

  if (urlFilter.trainingTrainingStatus) {
    filterModel.securityTraining = {
      filterType: 'trainingColumn',
      filterValues: {
        trainingStatus: String(urlFilter.trainingTrainingStatus),
      },
    };
  }

  if (urlFilter.coverageEndpointManagement === 'missing') {
    filterModel.endpointManagement = {
      filterType: 'endpointManagementCoverage',
      value: 'missing',
    };
  }

  if (urlFilter.coverageVulnerability === 'missing') {
    filterModel.vulnerabilityManagement = {
      filterType: 'vulnerabilityCoverage',
      value: 'missing',
    };
  }

  if (urlFilter.attributionStatus) {
    const values = String(urlFilter.attributionStatus)
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value === ATTRIBUTION_CONFLICT_LABEL || value === ATTRIBUTION_NO_CONFLICT_LABEL);

    if (values.length > 0) {
      filterModel.attributionStatus = {
        filterType: 'set',
        values,
      };
    }
  }

  // Expand the active insight into its canonical filter model entries. The insight is merged last,
  // so it owns its own column key: a stale same-column param (anything other than 'missing', the
  // only coverage value this grid can represent) can never block the insight's filter, and when
  // the param agrees the expansion is idempotent. Params for *other* columns still combine with the
  // insight (an additive column filter is intended); the tile click itself clears those, see
  // applyInsightTileFilters in AgGridToolingCompliance.tsx.
  const insightKey = urlFilter.insight as InsightKey | undefined;
  if (insightKey && INSIGHT_FILTER_MODELS[insightKey]) {
    Object.assign(filterModel, INSIGHT_FILTER_MODELS[insightKey]);
  }

  return { sortModel, filterModel };
}
